import styled, { css } from 'styled-components';
import WaDialog from '@web.awesome.me/webawesome-pro/dist/react/dialog';
import WaSkeleton from '@web.awesome.me/webawesome-pro/dist/react/skeleton';
import { xs } from '@exonet/react-shared/lib/styled';
import { hoverScale } from 'app/client/styled/animations';

// Dialog (webawesome parts)
export const Dialog = styled(WaDialog)`
  &:focus,
  &:focus-visible {
    outline: none;
    box-shadow: none;
  }

  &::part(dialog):focus,
  &::part(dialog):focus-visible {
    outline: none;
    box-shadow: none;
  }

  &::part(dialog) {
    width: 100vw;
    height: 100vh;
    max-width: 100vw;
    max-height: 100vh;
    margin: 0;
    border-radius: 0;

    background: linear-gradient(180deg, rgba(0, 0, 0, 0.04), rgba(0, 0, 0, 0)), var(--bg);
    color: var(--text);

    box-shadow: var(--box-shadow);

    display: flex;
    flex-direction: column;
    overflow: hidden;

    ${xs(css`
      width: min(920px, 100%);
      height: min(640px, 80vh);
      max-width: calc(100vw - 32px);
      max-height: calc(100vh - 64px);

      margin: 72px auto 0;
      border-radius: var(--border-radius);
      border: 1px solid var(--bg-3);
      box-shadow: var(--box-shadow);
    `)}
  }

  &::part(body) {
    padding: 0;

    // KEY: let slotted content become a flex column, so Body can scroll
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    min-height: 0;

    overflow: hidden;
  }

  &::part(overlay) {
    background-color: var(--modal-overlay-bg, #333);
  }
`;

// Header (search input)
export const Header = styled.div`
  display: grid;
  grid-template-columns: 44px 1fr 44px;
  align-items: center;
  gap: 10px;

  padding: 14px var(--sm-padding-horizontal); // was 10px
  background: var(--body-bg);
  border-bottom: 1px solid var(--bg-3);
`;

export const SearchIcon = styled.span`
  width: 44px;
  height: 44px;

  display: flex;
  align-items: center;
  justify-content: center;

  color: var(--text-muted);
  line-height: 1;
`;

export const SearchInput = styled.input`
  width: 100%;
  height: 44px;

  border: 0;
  outline: none;
  box-shadow: none;
  background: transparent;

  color: var(--title-text);
  font-size: var(--lg-font-size);

  padding: 0 4px;
  transform: translateY(1px); // 👈 subtle but important

  &:focus,
  &:focus-visible {
    outline: none;
    box-shadow: none;
  }

  &::placeholder {
    color: var(--text-muted);
  }
`;

export const IconButton = styled.button`
  width: 44px;
  height: 44px;

  border: 0;
  background: transparent;
  color: var(--text-muted);
  cursor: pointer;

  display: flex;
  align-items: center;
  justify-content: center;

  border-radius: var(--border-radius);
  transition:
    background-color 120ms ease,
    color 120ms ease;

  &:hover {
    background: var(--bg-2);
    color: var(--title-text);
  }

  &:focus,
  &:focus-visible {
    outline: none;
    box-shadow: none;
  }
`;

// Tabs
export const Tabs = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  touch-action: pan-x;

  padding: 12px var(--sm-padding-horizontal) 10px;
  background: var(--bg);
  border-bottom: 1px solid var(--bg-3);

  flex: 0 0 auto;
  flex-shrink: 0;
`;

export const TabButton = styled.button`
  border: 0;
  background: transparent;
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;

  color: var(--text-muted);
  font-size: var(--font-size);
  padding: 8px 4px; // was 10px 6px
  border-bottom: 2px solid transparent;

  transition:
    color 120ms ease,
    border-color 120ms ease;

  ${({ $active }) =>
    $active &&
    css`
      color: var(--title-text);
      border-bottom-color: var(--link);
    `}

  &:hover {
    color: var(--title-text);
  }

  &:focus {
    outline: none;
    box-shadow: none;
  }
`;

// Body (results region + overlays)
export const Body = styled.div`
  position: relative;

  flex: 1 1 auto;
  min-height: 0;
  overflow: hidden;

  background: var(--bg);
`;

export const ResultsScroll = styled.div`
  height: 100%;
  overflow-y: auto;
  scrollbar-gutter: stable;
  -webkit-overflow-scrolling: touch;
`;

export const ResultsOverlay = styled.div`
  position: absolute;
  inset: 0;

  display: flex;
  align-items: center;
  justify-content: center;

  background: color-mix(in srgb, var(--bg) 60%, transparent);

  z-index: 5;
  pointer-events: none;
`;

// Groups
export const Group = styled.section`
  &:not(:last-child) {
    margin-bottom: 18px;
  }
`;

export const GroupTitle = styled.div`
  font-size: var(--sm-font-size);
  color: var(--text-muted);
  margin: 20px 0 6px 0;
  letter-spacing: 0.02em;
  margin-left: var(--sm-padding-horizontal);
`;

export const GroupList = styled.div`
  display: flex;
  flex-direction: column;
`;

// Rows
export const RowRight = styled.div`
  flex: 0 0 auto;
  color: var(--text-muted);
  font-size: 18px;
  line-height: 1;
  opacity: 0;
  transition: opacity 120ms ease;
`;

export const Row = styled.button`
  width: 100%;
  background: transparent;
  border: none;
  color: inherit;
  cursor: pointer;

  padding: var(--sm-padding);
  border-radius: var(--border-radius);

  display: flex;
  align-items: center;
  justify-content: space-between;

  border-color 120ms ease,
  transform 120ms ease;

  &:hover {
    background-color: var(--bg-1);

    ${RowRight} {
      opacity: 1;
    }
  }

  &:active {
    transform: translateY(0);
  }

  &:focus {
    outline: none;
    box-shadow: none;
  }

  &:focus-visible {
    background-color: var(--bg-1);
    outline: 1px solid var(--focus-ring-color);
    outline-offset: -2px;
  }

  ${({ $active }) =>
    $active &&
    css`
      background-color: var(--bg-1);
      outline: 1px solid var(--focus-ring-color);
      outline-offset: -2px;

      ${RowRight} {
        opacity: 1;
      }
    `}
`;

export const RowLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  min-width: 0;
`;

export const RowIcon = styled.span`
  flex: 0 0 auto;

  ${hoverScale({
    hoverScaleValue: 1.15,
    activeScaleValue: 1.05,
    durationMs: 200,
    triggerSelector: Row,
    targetSelector: '&',
  })}
`;

export const RowText = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

export const RowTitle = styled.div`
  color: var(--title-text);
  font-size: var(--font-size);
  text-align: left;

  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const RowSubtitle = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;

  color: var(--text-muted);
  font-size: var(--sm-font-size);

  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const SubItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;

  min-width: 0;
  white-space: nowrap;
`;

// Loading skeleton (non-interactive stand-ins for the rows above). The row
// reuses the real Row chrome so the skeleton matches loaded rows by
// construction; pointer-events off so hover/active styles never trigger.
export const SkeletonRow = styled(Row).attrs({ as: 'div' })`
  pointer-events: none;
`;

// An icon-sized square placeholder (matches the lg FontAwesome icon in
// RowIcon). NOTE: keep the wa-skeleton host on its default flex display —
// the indicator fills the host via flex and collapses on a block host.
export const SkeletonIcon = styled(WaSkeleton)`
  width: 20px;
  height: 20px;

  &::part(indicator) {
    border-radius: var(--border-radius);
  }
`;

// Footer (hints)
export const Footer = styled.div`
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;

  padding: 10px var(--sm-padding-horizontal);
  background: var(--bg-1);
  border-top: 1px solid var(--bg-3);
  line-height: 1;
`;

export const FooterLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 18px;
  min-width: 0;
`;

export const FooterRight = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

export const FooterHint = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;

  color: var(--text-muted);
  font-size: var(--sm-font-size);
  white-space: nowrap;
`;

export const Keycap = styled.kbd`
  display: inline-flex;
  align-items: center;
  justify-content: center;

  height: 22px;
  min-width: 22px;
  padding: 0 8px;

  border-radius: var(--border-radius);
  border: 1px solid var(--bg-3);
  background: var(--bg);

  color: var(--title-text);
  font-size: var(--xs-font-size);
  font-family: var(--font-family-2);

  box-shadow: none;
`;

// Empty state
export const EmptyState = styled.div`
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 10px;
  opacity: 0.9;
`;

export const EmptyIcon = styled.div`
  opacity: 0.8;
  color: var(--link);
`;

export const NoResultIcon = styled.div`
  opacity: 0.8;
  color: var(--orange);
`;

export const EmptyTitle = styled.div`
  font-weight: 600;
  font-size: var(--lg-font-size);
`;

export const EmptySubtitle = styled.div`
  opacity: 0.75;
`;

// Status
export const StatusDot = styled.span`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex: 0 0 auto;
  margin-top: 1px;

  background: ${({ $variant }) =>
    ({
      success: 'var(--green)',
      warning: 'var(--orange)',
      danger: 'var(--red)',
      muted: 'var(--bg-4)',
    })[$variant] || 'var(--bg-4)'};
`;
